import { useDisplayTimezone } from "@nexus/ui";
import { useCallback, useMemo } from "react";
import {
  defineLabels,
  resolveLabels,
  firstError,
  unwrap,
  Widget,
  WidgetSkeleton,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type { JsonValue, WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import {
  formatClock,
  formatDayClock,
  formatRelative,
  isOngoing,
  LiveDot,
  orderChannels,
  parseDbTime,
  type EventTimelineData,
  type TimelineProtectedEvent,
} from "@nexus/widget-kit";

export type { EventTimelineData, TimelineEvent, TimelineProtectedEvent } from "@nexus/widget-kit";

/* ------------------------------------------------------------------ */
/*  Inline icons                                                      */
/* ------------------------------------------------------------------ */

function AlertTriangle({ className }: { className?: string }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M8.66 2.57a.75.75 0 0 0-1.32 0L1.18 13.1a.75.75 0 0 0 .66 1.1h12.32a.75.75 0 0 0 .66-1.1L8.66 2.57Z"
        fill="currentColor"
        fillOpacity=".15"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M8 6.5v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="8" cy="11.5" r=".7" fill="currentColor" />
    </svg>
  );
}

function ChevronRight({ className }: { className?: string }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="m6 3 5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Row surface helper                                                */
/* ------------------------------------------------------------------ */

function rowSurface(selected: boolean, interactive: boolean): string {
  const base =
    "flex items-center gap-3 rounded-lg border border-border px-3 py-2 transition-colors duration-100";

  if (selected) {
    return `${base} border-brand-cyan/70 bg-brand-cyan/10`;
  }
  if (interactive) {
    return `${base} hover:border-border hover:bg-surface-elevated`;
  }
  return base;
}

/* ------------------------------------------------------------------ */
/*  Manifest & config                                                 */
/* ------------------------------------------------------------------ */

export interface EventScopeConfig {
  title?: string;
  interactionType?: string;
}

export const LABELS = defineLabels([
  {
    key: "noEventTitle",
    name: "No event selected: title",
    default: "No event specified",
  },
  {
    key: "noEventDescription",
    name: "No event selected: description",
    default: "Click an event on the timeline to open it here.",
  },
  {
    key: "notFoundTitle",
    name: "Event not found: title",
    default: "Event not found",
  },
  {
    key: "notFoundDescription",
    name: "Event not found: description",
    default: "The requested event could not be loaded.",
  },
  {
    key: "channelsHeading",
    name: "Section heading: Channels",
    default: "Channels",
  },
  {
    key: "noChannels",
    name: "Message when no channels exist",
    default: "No protected events configured.",
  },
  {
    key: "liveNow",
    name: "Status text while the event is live",
    default: "Live now",
  },
] as const);

export const manifest: WidgetManifest = {
  id: "event-scope",
  name: "Event Scope",
  description: "Current event context with protected-event channels for dashboard filtering.",
  version: "1.0.0",
  labels: LABELS,
  dataRequests: {
    event: {
      source: "event-scope",
      contract: { name: "event-scope", version: 1 },
      configurable: false,
    },
  },
};

export const defaultConfig: EventScopeConfig = {
  title: "Current Event",
  interactionType: "event.scope",
};

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

function EventScopeComponent({ data, config, dashboard }: WidgetProps<EventScopeConfig>) {
  const timezone = useDisplayTimezone();
  const labels = resolveLabels(LABELS, config);
  const error = firstError(data, ["event"]);
  const resolved = unwrap<EventTimelineData>(data.event);
  const isLoading = data.event?.isLoading ?? false;
  const interactive = typeof dashboard.emitInteraction === "function";
  const title = config.title ?? defaultConfig.title;

  const currentEvent = resolved?.events?.[0] ?? null;

  const requestedEventId = dashboard.filters.dataFilters.find((f) => f.field === "event_id")
    ?.value as string | undefined;

  const selectedProtectedEventId = dashboard.filters.dataFilters.find(
    (f) => f.field === "protected_event_id",
  )?.value as string | undefined;

  const nowMs = Date.now();

  const channels = useMemo(
    () => (currentEvent ? orderChannels(currentEvent.protected_events) : []),
    [currentEvent],
  );

  const interactionType = config.interactionType ?? defaultConfig.interactionType!;

  const handleSelect = useCallback(
    (pe: TimelineProtectedEvent) => {
      if (!currentEvent) return;

      dashboard.emitInteraction?.({
        type: interactionType,
        payload: {
          scope: "protected-event",
          event_id: currentEvent.event_id,
          event_label: currentEvent.event_label,
          protected_event_id: pe.protected_event_id,
          tenant_id: pe.tenant_id,
          channel: pe.channel,
          channel_count: channels.length,
        } as JsonValue,
        source: { dataKey: "event" },
      });
    },
    [channels.length, currentEvent, dashboard, interactionType],
  );

  /* ---------- empty states ---------- */

  if (error) {
    return (
      <Widget title={title}>
        <WidgetIssueState error={error} />
      </Widget>
    );
  }

  if (!requestedEventId) {
    return (
      <Widget title={title}>
        <EmptyState title={labels.noEventTitle} subtitle={labels.noEventDescription} />
      </Widget>
    );
  }

  if (!currentEvent) {
    return (
      <Widget title={title}>
        {isLoading ? (
          <WidgetSkeleton shape="kpis" />
        ) : (
          <EmptyState title={labels.notFoundTitle} subtitle={labels.notFoundDescription} />
        )}
      </Widget>
    );
  }

  /* ---------- derived values ---------- */

  const live = isOngoing(currentEvent, nowMs);
  const start = parseDbTime(currentEvent.event_start);
  const end = parseDbTime(currentEvent.event_end);
  const anomalyTotal = currentEvent.unresolved_anomaly_count;

  /* ---------- render ---------- */

  return (
    <Widget title={title}>
      <div className="grid h-full min-h-0 gap-3 density-widget-body @4xl/widget:grid-cols-[minmax(14rem,0.75fr)_minmax(0,3fr)] @4xl/widget:items-end">
        <header className="min-w-0">
          <p className="truncate text-sm font-semibold text-primary">{currentEvent.event_label}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <div className="flex items-center gap-1.5">
              {live && <LiveDot tone={anomalyTotal > 0 ? "error" : "accent"} />}
              <span className="text-xs font-medium text-muted">
                {live
                  ? labels.liveNow
                  : start <= nowMs
                    ? `Ended ${formatRelative(nowMs, end)}`
                    : `Starts ${formatRelative(nowMs, start)}`}
              </span>
            </div>
            <p className="text-xs tabular-nums text-muted">
              {formatDayClock(start, timezone)}&ensp;{formatClock(start, timezone)}–
              {formatClock(end, timezone)}
            </p>
          </div>
        </header>

        <section className="min-w-0">
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-xs font-medium text-secondary">
              {labels.channelsHeading}
              <span className="ml-1 opacity-40">({channels.length})</span>
            </p>
          </div>

          {channels.length > 0 ? (
            <ul className="grid min-h-0 grid-cols-1 gap-2 overflow-y-auto @xl/widget:grid-cols-2 @4xl/widget:grid-cols-3">
              {channels.map((pe) => {
                const selected = selectedProtectedEventId === pe.protected_event_id;
                const hasAnomaly = pe.unresolved_anomaly_count > 0;

                const content = (
                  <>
                    <span className="truncate text-sm text-primary">{pe.channel || "—"}</span>

                    <span className="ml-auto shrink-0 text-xs text-muted">{pe.tenant_label}</span>

                    {hasAnomaly && (
                      <span className="flex shrink-0 items-center gap-1 text-status-error-text">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        <span className="text-xs font-semibold tabular-nums">
                          {pe.unresolved_anomaly_count}
                        </span>
                      </span>
                    )}
                  </>
                );

                return (
                  <li key={pe.protected_event_id}>
                    {interactive ? (
                      <button
                        type="button"
                        onClick={() => handleSelect(pe)}
                        aria-pressed={selected}
                        className={[
                          rowSurface(selected, true),
                          "group w-full cursor-pointer text-left",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus",
                        ].join(" ")}
                      >
                        {content}
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted/50 transition-transform group-hover:translate-x-0.5" />
                        <span className="sr-only">Scope dashboard to {pe.channel}</span>
                      </button>
                    ) : (
                      <div className={rowSurface(selected, false)}>{content}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="py-6 text-center text-sm text-muted">{labels.noChannels}</p>
          )}
        </section>
      </div>
    </Widget>
  );
}

/* ------------------------------------------------------------------ */
/*  Empty-state helper                                                */
/* ------------------------------------------------------------------ */

function EmptyState({
  title,
  subtitle,
  tone = "default",
}: {
  title: string;
  subtitle: string;
  tone?: "default" | "error";
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 px-6 py-10 text-center">
      <p
        className={`text-sm font-medium ${tone === "error" ? "text-status-error-text" : "text-primary"}`}
      >
        {title}
      </p>
      <p className="text-xs text-muted">{subtitle}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Module export                                                     */
/* ------------------------------------------------------------------ */

export const Component = EventScopeComponent;

const widgetModule: WidgetModule<EventScopeConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
